// The admin's Anthropic key management - lives in the Anthropic row's
// gear dialog on /settings/integrations. The server stores the key sealed
// and never echoes it; the form is replace-only.

import { useState } from "react";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import {
  useAnthropicConfig,
  useSaveAnthropicKey,
  useRemoveAnthropicKey,
  useTestAnthropicKey,
} from "../../hooks/useAnthropicConfig";
import type { AnthropicConfigStatus } from "../../api/integrations";
import { SECRET_ON_FILE } from "./secretOnFile";

function anthropicStatusLine(s: AnthropicConfigStatus): string {
  if (s.unreadable) {
    return "The stored key no longer decrypts - the server encryption key changed. Enter the key again.";
  }
  if (!s.configured) return "No API key - every AI feature is dark.";
  return s.source === "db" ? "Key saved in Settings." : "Key from the server environment.";
}

export function AnthropicKeySection() {
  // Mounted from the admin-only gear, but the flag stands on its own:
  // wherever this section lands, a non-admin never issues the GET.
  const user = useCurrentUser();
  const configQ = useAnthropicConfig(isAdminRole(user?.role));
  const save = useSaveAnthropicKey();
  const remove = useRemoveAnthropicKey();
  const testConn = useTestAnthropicKey();
  const [apiKey, setApiKey] = useState("");

  const status = configQ.data;
  const canSave = Boolean(status?.canStore) && apiKey.trim() !== "" && !save.isPending;

  function onSave() {
    // The probe verdict belongs to the key that was tested, and a save or a
    // removal replaces that key.
    testConn.reset();
    save.mutate(apiKey.trim(), { onSuccess: () => setApiKey("") });
  }

  function onRemove() {
    const ok = window.confirm(
      "Remove the stored Anthropic key? Every AI feature stops until a new key is saved; " +
        "a key from the server environment, if any, applies again.",
    );
    if (!ok) return;
    testConn.reset();
    save.reset();
    remove.mutate();
  }

  return (
    <section className="settings-section">
      <p className="settings-sub">
        The API key behind document extraction, drafting and the assistant. Stored encrypted on the
        server and never shown back - enter a value to replace the one on file.
      </p>

      {configQ.isLoading && (
        <SkeletonGroup>
          <Skeleton width={220} />
          <Skeleton width={180} />
        </SkeletonGroup>
      )}
      {configQ.isError && (
        <p className="settings-error">Could not load the AI settings. Refresh to retry.</p>
      )}

      {status && (
        <>
          <p className="settings-sub" role="status">
            {anthropicStatusLine(status)}
          </p>
          {status.source === "db" && status.updatedAt && (
            <p className="settings-hint">
              Saved {new Date(status.updatedAt).toLocaleDateString()}
              {status.updatedByEmail ? ` by ${status.updatedByEmail}` : ""}
            </p>
          )}
          {!status.canStore && (
            <p className="settings-error">
              Key storage is off - set ENCRYPTION_KEY on the server to save the key here.
            </p>
          )}
        </>
      )}

      <div className="settings-grid">
        <label className="settings-field">
          <span className="settings-label">API key</span>
          <input
            className="cc-field"
            type="password"
            autoComplete="new-password"
            placeholder={status?.configured ? SECRET_ON_FILE : "sk-ant-..."}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          {status?.configured && (
            <span className="settings-hint">A key is on file - saving replaces it.</span>
          )}
        </label>
      </div>

      <div className="settings-actions">
        <button type="button" className="settings-save-btn" disabled={!canSave} onClick={onSave}>
          {save.isPending ? "Saving..." : "Save key"}
        </button>
        {status?.source === "db" && (
          <button
            type="button"
            className="cc-link-btn danger"
            disabled={remove.isPending}
            onClick={onRemove}
          >
            Remove key
          </button>
        )}
        {status?.configured && (
          <button
            type="button"
            className="cc-link-btn"
            disabled={testConn.isPending}
            onClick={() => testConn.mutate()}
          >
            {testConn.isPending ? "Testing..." : "Test connection"}
          </button>
        )}
      </div>

      {save.isError && <p className="settings-error">{(save.error as Error).message}</p>}
      {remove.isError && <p className="settings-error">{(remove.error as Error).message}</p>}
      {save.isSuccess && (
        <p className="settings-ok" role="status">
          Key saved.
        </p>
      )}
      {testConn.data?.ok && (
        <p className="settings-ok" role="status">
          The key works - Anthropic answered a test request.
        </p>
      )}
      {testConn.data && !testConn.data.ok && (
        <p className="settings-error">
          Anthropic refused the request ({testConn.data.errorCode}).
          {testConn.data.reason ? ` ${testConn.data.reason}` : ""}
        </p>
      )}
      {testConn.isError && <p className="settings-error">{(testConn.error as Error).message}</p>}
    </section>
  );
}
